import { FiMessageCircle } from "react-icons/fi";
import { useDispatch, useSelector } from "react-redux";
import styled, { css } from "styled-components";
import { BooleanParam, useQueryParams } from "use-query-params";

import { trackEvent } from "../../helperFunctions";
import { useChatStatus } from "../../hooks/useChatStatus";
import { useProviderChatStatus } from "../../hooks/useProviderChatStatus";
import FlexBox from "../common/FlexBox";
import {
  BRICK_TERRACOTA_200,
  BRICK_TERRACOTA_400,
  BRICK_TERRACOTA_800,
  DAVYS_GREY_400,
  DAVYS_GREY_800,
} from "../common/colors";
import { boxShadowDs2 } from "../common/styles";

const Button = styled(FlexBox)`
  visibility: ${({ isChatOpen }) => (isChatOpen ? "hidden" : "visible")};
  width: 3rem;
  height: 3rem;
  border-radius: 50%;
  align-items: center;
  justify-content: center;
  background-color: ${({ unreadMessage }) =>
    unreadMessage ? BRICK_TERRACOTA_200 : "white"};
  border: 1px solid
    ${({ unreadMessage }) =>
      unreadMessage ? BRICK_TERRACOTA_400 : DAVYS_GREY_400};
  color: ${DAVYS_GREY_800};
  cursor: pointer;
  ${boxShadowDs2}
  &:hover {
    transform: scale(1.1);
  }
  z-index: 10;
`;

const ChatIcon = styled.div`
  position: relative;
  ${({ hasUnreadMessages }) =>
    hasUnreadMessages &&
    css`
      &::after {
        content: "";
        position: absolute;
        width: 0.5rem;
        height: 0.5rem;
        border-radius: 50%;
        top: 0;
        right: 0;
        background-color: ${BRICK_TERRACOTA_800};
      }
    `}
`;

const ChatButton = ({ hasUnreadMessages }) => {
  const dispatch = useDispatch();
  const [, setQueryParams] = useQueryParams({
    showChatList: BooleanParam,
  });

  const user = useSelector(state => state?.auth?.user);
  const userFBId = user?.firebaseid;
  const isNewChatEnabled = useSelector(
    state => !!state.provider?.providerProfile?.new_chat_enabled
  );
  const newChatUnreadRoomCount = useSelector(
    state => state.chat?.newChatUnreadRoomIds?.length || 0
  );

  const { providerChatStatus } = useProviderChatStatus(userFBId);

  const isChatOpen = useSelector(state => state.chat.isChatOpen);
  const reduxChatList = useSelector(state => state?.chat?.chatList);
  const { chatStatus } = useChatStatus(userFBId);
  const isOffline = chatStatus?.toLowerCase() === "offline";

  const unread_chat_count = reduxChatList?.filter(
    chat => chat.client && chat.client.unreadCount > 0
  );
  const unreadChatCount = isNewChatEnabled
    ? newChatUnreadRoomCount
    : unread_chat_count.length;

  const chatEventPayload = {
    unread_chat_count: unreadChatCount,
    chat_availability: isOffline ? "Offline" : "Online",
    chat_status: providerChatStatus || "null",
    is_auto_reply_sent:
      (providerChatStatus &&
        !["Available", "In Session"].includes(providerChatStatus)) ||
      false,
    source: "dashboard",
  };

  const toggleChatBlock = () => {
    setQueryParams({ showChatList: 1 }, "replaceIn");
    dispatch({ type: "TOGGLE_CHAT_BLOCK" });
    trackEvent({
      event: "twa_chat_cta_click",
      payload: chatEventPayload,
    });
  };

  return (
    <Button
      id="chat-button"
      isChatOpen={isChatOpen}
      onClick={toggleChatBlock}
      unreadMessage={hasUnreadMessages}
    >
      <ChatIcon hasUnreadMessages={hasUnreadMessages}>
        <FiMessageCircle size="1.25rem" />
      </ChatIcon>
    </Button>
  );
};

export default ChatButton;
